/**
 * ApprovalsPanel.jsx — approvals UI (FE Contract v2.0 §7).
 *
 * Two exports:
 *   <ApprovalBadge jobId … />   badge + submit + audit timeline for one job
 *   <ApprovalsQueue me … />     the approver queue (gate on canApprove before mounting)
 *
 * §7 scope: approval is ADVISORY. Nothing here disables export — the badge only
 * informs. See APPROVAL_GATES_EXPORT in approvals.js.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTheme } from './theme';
import {
  getApprovalForJob, submitForApproval, decideApproval, listApprovals,
  deriveApprovalBadge, canDecide, canApprove, validateDecision,
  isJobApprovable, ApprovalsApiError,
} from './approvals';

/* ── shared bits ──────────────────────────────────────────────────────── */

function toneColors(tone, P) {
  switch (tone) {
    case 'ok': return { fg: P.ok, bg: P.okSoft, line: P.okLine };
    case 'danger': return { fg: P.danger, bg: P.dangerSoft, line: P.dangerLine };
    case 'warn': return { fg: P.warn, bg: P.warnSoft, line: P.warnLine };
    case 'pending': return { fg: P.accent, bg: P.accentSoft, line: P.accentLine };
    default: return { fg: P.muted, bg: 'transparent', line: P.rule };
  }
}

function Pill({ tone, children }) {
  const { P, MO } = useTheme();
  const c = toneColors(tone, P);
  return (
    <span style={{
      fontFamily: MO, fontSize: 9.5, letterSpacing: 0.6, textTransform: 'uppercase',
      padding: '3px 8px', borderRadius: 4, color: c.fg,
      background: c.bg, border: `0.5px solid ${c.line}`, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

function SmallBtn({ children, onClick, disabled, tone = 'default', title }) {
  const { P, FN } = useTheme();
  const c = toneColors(tone === 'default' ? 'neutral' : tone, P);
  return (
    <button onClick={onClick} disabled={disabled} title={title} style={{
      fontFamily: FN, fontSize: 11.5, padding: '5px 11px', borderRadius: 5,
      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
      background: tone === 'default' ? P.ink : c.bg,
      color: tone === 'default' ? P.sheet : c.fg,
      border: `0.5px solid ${tone === 'default' ? P.ink : c.line}`,
    }}>{children}</button>
  );
}

function fmtWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
}

/* ── decision form (approve / reject) ─────────────────────────────────── */

function DecisionForm({ onDecide, busy }) {
  const { P, FN, MO, inp } = useTheme();
  const [comment, setComment] = useState('');
  const [err, setErr] = useState(null);

  const go = (decision) => {
    // §7.2: a rejection must carry a reason — caught here so the user never
    // eats a 422 round-trip.
    const invalid = validateDecision({ decision, comment });
    if (invalid) { setErr(invalid); return; }
    setErr(null);
    onDecide(decision, comment);
  };

  return (
    <div style={{ marginTop: 10 }}>
      <label style={{ fontFamily: MO, fontSize: 10, color: P.muted, display: 'block', marginBottom: 4 }}>
        Comment <span style={{ color: P.dim }}>(required to reject)</span>
      </label>
      <textarea value={comment} onChange={(e) => { setComment(e.target.value); if (err) setErr(null); }}
        rows={2} placeholder="Reason for the decision…"
        style={{ ...inp, width: '100%', resize: 'vertical', fontFamily: FN, fontSize: 12 }} />
      {err && <div style={{ fontFamily: MO, fontSize: 10, color: P.danger, marginTop: 4 }}>⚠ {err}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <SmallBtn tone="ok" onClick={() => go('approved')} disabled={busy}>
          {busy ? 'Working…' : 'Approve'}
        </SmallBtn>
        <SmallBtn tone="danger" onClick={() => go('rejected')} disabled={busy}>Reject</SmallBtn>
      </div>
    </div>
  );
}

/* ── history timeline (§7.1 — pharma customers will ask for this) ─────── */

function HistoryTimeline({ history }) {
  const { P, FN, MO } = useTheme();
  if (!history.length) return null;
  return (
    <div style={{ marginTop: 12, paddingTop: 10, borderTop: `0.5px solid ${P.ruleSoft || P.rule}` }}>
      <div style={{ fontFamily: MO, fontSize: 9, color: P.dim, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
        Audit trail
      </div>
      {history.map((h, i) => (
        <div key={h.id || i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 5 }}>
          <Pill tone={h.state === 'approved' ? 'ok' : h.state === 'rejected' ? 'danger' : 'pending'}>{h.state}</Pill>
          <span style={{ fontFamily: FN, fontSize: 11, color: P.muted, lineHeight: 1.45 }}>
            {h.submitted_by && <>submitted by {h.submitted_by}</>}
            {h.decided_by && <> · decided by {h.decided_by}</>}
            {h.decided_at && <> · {fmtWhen(h.decided_at)}</>}
            {h.comment && <> — “{h.comment}”</>}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ── the badge ────────────────────────────────────────────────────────── */

/**
 * Approval badge for one succeeded job.
 *
 * @param jobId   the succeeded job's id (plan or export)
 * @param job     the job snapshot — used only to predict §7.2 approvability
 * @param me      current user (auth.user)
 */
export function ApprovalBadge({ jobId, job, me }) {
  const { P, FN, MO } = useTheme();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState('');
  const [showSubmit, setShowSubmit] = useState(false);
  // §7.3: "treat a 403 as authoritative and disable on first refusal" — some
  // deployments relax the four-eyes rule, so a local prediction is not final.
  const [refused, setRefused] = useState(false);
  // Unsupported backend (pre-§7) → hide the whole surface rather than nag.
  const [unsupported, setUnsupported] = useState(false);

  const load = useCallback(async () => {
    if (!jobId) return;
    setLoading(true); setError(null);
    try {
      setPayload(await getApprovalForJob({ jobId }));
    } catch (e) {
      if (e instanceof ApprovalsApiError && (e.status === 404 || e.status === 405)) setUnsupported(true);
      else setError(e instanceof ApprovalsApiError ? e.message : String(e));
    } finally { setLoading(false); }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  if (!jobId || unsupported) return null;

  const badge = deriveApprovalBadge(payload);
  const current = payload?.current ?? null;
  const history = payload?.history ?? [];
  const mayDecide = canDecide(current, me, refused);
  const approvable = isJobApprovable(job);
  const ownPending = current?.state === 'submitted' && me && current.submitted_by === me.id;

  const doSubmit = async () => {
    setBusy(true); setError(null);
    try {
      await submitForApproval({ jobId, note });
      setNote(''); setShowSubmit(false);
      await load();
    } catch (e) {
      // §8: 409 already_pending just means we're stale — refresh rather than shout.
      if (e instanceof ApprovalsApiError && e.code === 'already_pending') await load();
      else setError(e instanceof ApprovalsApiError ? e.message : String(e));
    } finally { setBusy(false); }
  };

  const doDecide = async (decision, comment) => {
    if (!current?.id) return;
    setBusy(true); setError(null);
    try {
      await decideApproval({ approvalId: current.id, decision, comment });
      await load();
    } catch (e) {
      if (e instanceof ApprovalsApiError && e.code === 'self_approval_forbidden') {
        setRefused(true);                       // §7.3 — authoritative
        setError('You can’t decide your own submission — another approver must review it.');
      } else if (e instanceof ApprovalsApiError && e.code === 'already_decided') {
        await load();                           // someone got there first
      } else {
        setError(e instanceof ApprovalsApiError ? e.message : String(e));
      }
    } finally { setBusy(false); }
  };

  const c = toneColors(badge.tone, P);

  return (
    <section style={{
      marginTop: 14, padding: '12px 14px', borderRadius: 8,
      background: P.elev, border: `0.5px solid ${P.rule}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: MO, fontSize: 9, color: P.dim, textTransform: 'uppercase', letterSpacing: 0.6 }}>Approval</span>
        <Pill tone={badge.tone}>{loading ? 'Checking…' : badge.label}</Pill>
        {badge.detail && (
          <span style={{ fontFamily: FN, fontSize: 11.5, color: badge.stale ? c.fg : P.muted, lineHeight: 1.4 }}>
            {badge.detail}
          </span>
        )}
      </div>

      {/* §7: advisory only — we discourage, never block. */}
      {(badge.state === 'none' || badge.state === 'rejected' || badge.stale) && (
        <div style={{ fontFamily: FN, fontSize: 10.5, color: P.dim, marginTop: 6, lineHeight: 1.45 }}>
          Export isn’t blocked by approval — this is a record, not a gate.
        </div>
      )}

      {error && <div style={{ fontFamily: MO, fontSize: 10, color: P.danger, marginTop: 8 }}>⚠ {error}</div>}

      {/* Submit — only for succeeded jobs (§7.2 predicts 409 job_not_approvable). */}
      {badge.canSubmit && approvable && (
        showSubmit ? (
          <div style={{ marginTop: 10 }}>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for the approver (optional)"
              style={{
                width: '100%', fontFamily: 'inherit', fontSize: 12, padding: '6px 8px',
                borderRadius: 5, border: `0.5px solid ${P.rule}`, background: P.sheet, color: P.ink,
              }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <SmallBtn onClick={doSubmit} disabled={busy}>{busy ? 'Submitting…' : 'Submit'}</SmallBtn>
              <SmallBtn tone="neutral" onClick={() => setShowSubmit(false)} disabled={busy}>Cancel</SmallBtn>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 10 }}>
            <SmallBtn onClick={() => setShowSubmit(true)}>Submit for approval</SmallBtn>
          </div>
        )
      )}

      {/* Decide — §7.3 four-eyes: never render a control that is certain to fail. */}
      {mayDecide && <DecisionForm onDecide={doDecide} busy={busy} />}
      {ownPending && (
        <div style={{ fontFamily: FN, fontSize: 11, color: P.muted, marginTop: 8 }}>
          Awaiting another approver — you can’t decide your own submission.
        </div>
      )}

      <HistoryTimeline history={history} />
    </section>
  );
}

/* ── the approver queue ───────────────────────────────────────────────── */

/**
 * Approver queue (§7.2). Mount only when canApprove(me) — the caller gates the
 * nav; this also guards defensively.
 */
export function ApprovalsQueue({ me, onOpenJob }) {
  const { P, FN, MO } = useTheme();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('queue');   // 'queue' | 'mine'
  // Per-row decide state.
  const [expandedId, setExpandedId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [rowError, setRowError] = useState(null);
  const [errorFor, setErrorFor] = useState(null);
  // §7.3: a real 403 is authoritative — latch that row's controls off.
  const [refusedIds, setRefusedIds] = useState([]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setItems(tab === 'queue'
        ? await listApprovals({ state: 'submitted' })
        : await listApprovals({ mine: true }));
    } catch (e) {
      setError(e instanceof ApprovalsApiError ? e.message : String(e));
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const decide = async (a, decision, comment) => {
    setBusyId(a.id); setRowError(null); setErrorFor(null);
    try {
      await decideApproval({ approvalId: a.id, decision, comment });
      setExpandedId(null);
      await load();
    } catch (e) {
      const code = e instanceof ApprovalsApiError ? e.code : null;
      if (code === 'self_approval_forbidden') {
        setRefusedIds((prev) => [...prev, a.id]);
        setRowError('You can’t decide your own submission — another approver must review it.');
        setErrorFor(a.id);
      } else if (code === 'already_decided') {
        await load();                         // someone got there first
      } else {
        setRowError(e instanceof ApprovalsApiError ? e.message : String(e));
        setErrorFor(a.id);
      }
    } finally { setBusyId(null); }
  };

  if (!canApprove(me) && tab === 'queue') {
    return (
      <div style={{ fontFamily: FN, fontSize: 13, color: P.muted, padding: 24 }}>
        You don’t have permission to review approvals.
      </div>
    );
  }

  const tabStyle = (on) => ({
    fontFamily: MO, fontSize: 10, letterSpacing: 0.5, textTransform: 'uppercase',
    padding: '5px 10px', borderRadius: 5, cursor: 'pointer',
    background: on ? P.ink : 'transparent', color: on ? P.sheet : P.muted,
    border: `0.5px solid ${on ? P.ink : P.rule}`,
  });

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <button style={tabStyle(tab === 'queue')} onClick={() => setTab('queue')}>Pending queue</button>
        <button style={tabStyle(tab === 'mine')} onClick={() => setTab('mine')}>My submissions</button>
        <SmallBtn tone="neutral" onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</SmallBtn>
      </div>

      {error && <div style={{ fontFamily: MO, fontSize: 11, color: P.danger, marginBottom: 10 }}>⚠ {error}</div>}

      {!loading && !items.length && (
        <div style={{ fontFamily: FN, fontSize: 13, color: P.muted }}>
          {tab === 'queue' ? 'Nothing awaiting review.' : 'You haven’t submitted anything yet.'}
        </div>
      )}

      {items.map((a) => {
        const b = deriveApprovalBadge({ current: a });
        const mine = me && a.submitted_by === me.id;
        // §7.3: never render a decide control that is certain to 403.
        const mayDecide = canDecide(a, me, refusedIds.includes(a.id));
        const open = expandedId === a.id;
        return (
          <div key={a.id} style={{
            padding: '10px 14px', marginBottom: 8, borderRadius: 8,
            background: P.elev, border: `0.5px solid ${P.rule}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Pill tone={b.tone}>{b.label}</Pill>
              <span style={{ fontFamily: MO, fontSize: 11, color: P.ink }}>{a.job_id}</span>
              <span style={{ fontFamily: FN, fontSize: 11, color: P.muted }}>
                {a.submitted_by && <>by {a.submitted_by}</>}
                {a.created_at && <> · {fmtWhen(a.created_at)}</>}
                {a.note && <> — “{a.note}”</>}
              </span>
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                {onOpenJob && (
                  <SmallBtn tone="neutral" onClick={() => onOpenJob(a.job_id)}
                    title="Load this layout into Auto Imposer to inspect it">
                    Load &amp; inspect
                  </SmallBtn>
                )}
                {mayDecide && (
                  <SmallBtn onClick={() => setExpandedId(open ? null : a.id)}>
                    {open ? 'Close' : 'Review'}
                  </SmallBtn>
                )}
              </span>
            </div>

            {/* Decide inline — an approver shouldn't have to leave the queue. */}
            {mayDecide && open && (
              <DecisionForm busy={busyId === a.id} onDecide={(decision, comment) => decide(a, decision, comment)} />
            )}
            {mine && a.state === 'submitted' && (
              <div style={{ fontFamily: FN, fontSize: 11, color: P.muted, marginTop: 8 }}>
                Awaiting another approver — you can’t decide your own submission.
              </div>
            )}
            {errorFor === a.id && rowError && (
              <div style={{ fontFamily: MO, fontSize: 10, color: P.danger, marginTop: 8 }}>⚠ {rowError}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
